/**
 * NVS (National Vehicle Service) Service
 * Handles NVS API calls for lien, theft, impound, and other vehicle event data
 */

import { makeAuthenticatedRequest, createApiError, getErrorMessage } from './apiService';
import { API_ENDPOINTS } from '../config/api';

// TypeScript interfaces matching backend DTOs

export interface NvsSearchRequest {
  vin: string;
}

export interface NvsLien {
  vin?: string;
  lienholder?: string;
  lienDate?: string;
}

export interface NvsImpound {
  vin?: string;
  impoundDate?: string;
  state?: string;
}

export interface NvsExport {
  vin?: string;
  exportDate?: string;
  state?: string;
}

export interface NvsStolenSummary {
  vin?: string;
  status?: string;
  lastEventDate?: string;
  state?: string;
}

export interface NvsEbayAuction {
  vin?: string;
  itemNumber?: string;
  auctionDate?: string;
}

export interface NvsRecall {
  vin?: string;
  reason?: string;
}

export interface NvsCustomsInquiry {
  vin?: string;
  searchDate?: string;
}

export interface NvsError {
  sourceSearchId?: string;
  code: number;
  message?: string;
}

export interface NvsEventData {
  version: number;
  liens: NvsLien[];
  impounds: NvsImpound[];
  exports: NvsExport[];
  stolenRecords: NvsStolenSummary[];
  ebayAuctions: NvsEbayAuction[];
  recalls: NvsRecall[];
  customsInquiries: NvsCustomsInquiry[];
  errors: NvsError[];
  rawSoapResponse?: string;
}

export interface NvsSearchResponse {
  reportId: string;
  vin: string;
  fetchedAt: string;
  wasCached: boolean;
  billingAmount: number;
  data?: NvsEventData;
}

export interface NvsEventSummary {
  lienCount: number;
  impoundCount: number;
  exportCount: number;
  hasStolenSummary: boolean;
  stolenStatus?: string;
  ebayAuctionCount: number;
  hasRecall: boolean;
  customsInquiryCount: number;
}

export interface NvsReportSummary {
  id: string;
  vin: string;
  fetchedAt: string;
  lastAccessedAt: string;
  daysUntilExpiration: number;
  isFresh: boolean;
  nvsCost: number;
  eventSummary?: NvsEventSummary;
}

export interface NvsReportListResponse {
  reports: NvsReportSummary[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface NvsPricingResponse {
  costPerVin: number;
  serviceType: string;
}

export interface NvsBulkSearchItem {
  vin: string;
  referenceNote?: string;
}

export interface NvsBulkSearchResultItem {
  vin: string;
  referenceNote?: string;
  success: boolean;
  errorMessage?: string;
  reportId?: string;
  wasCached: boolean;
  billingAmount: number;
  fetchedAt?: string;
  data?: NvsEventData;
}

export interface NvsBulkSearchResponse {
  totalRequested: number;
  totalSucceeded: number;
  totalFailed: number;
  totalBillingAmount: number;
  results: NvsBulkSearchResultItem[];
}

/**
 * Fetch NVS pricing (flat rate per VIN lookup)
 * Used to display estimated cost before search
 */
export const fetchNvsPricing = async (): Promise<NvsPricingResponse> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NVS.PRICING,
    { method: 'GET' }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Search NVS for vehicle event data by VIN
 * Returns cached data if available and fresh, otherwise fetches from NVS
 */
export const searchNvsByVin = async (vin: string): Promise<NvsSearchResponse> => {
  try {
    const response = await makeAuthenticatedRequest(API_ENDPOINTS.NVS.SEARCH, {
      method: 'POST',
      body: JSON.stringify({ vin }),
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: NvsSearchResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Bulk NVS search — submit up to 50 VINs in a single request
 */
export const bulkSearchNvsByVins = async (items: NvsBulkSearchItem[]): Promise<NvsBulkSearchResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NVS.BULK_SEARCH, {
    method: 'POST',
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Get a list of NVS reports for the authenticated user/client
 */
export const getNvsReports = async (
  pageNumber: number = 1,
  pageSize: number = 20,
  vin?: string
): Promise<NvsReportListResponse> => {
  try {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });

    if (vin) {
      params.append('vin', vin);
    }

    const url = `${API_ENDPOINTS.NVS.REPORTS}?${params.toString()}`;
    const response = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: NvsReportListResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get a specific NVS report by ID
 */
export const getNvsReportById = async (reportId: string): Promise<NvsSearchResponse> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.NVS.GET_REPORT(reportId),
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: NvsSearchResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function to extract summary information from NVS event data
 */
export const extractNvsInfo = (data?: NvsEventData) => {
  if (!data) return null;

  const liens = data.liens || [];
  const impounds = data.impounds || [];
  const exports = data.exports || [];
  const stolenRecords = data.stolenRecords || [];
  const ebayAuctions = data.ebayAuctions || [];
  const recalls = data.recalls || [];
  const customsInquiries = data.customsInquiries || [];
  const errors = data.errors || [];

  // Determine if there are any critical issues
  const hasActiveStolenRecord = stolenRecords.some(r => r.status === 'Active');
  const hasLienRecords = liens.length > 0;
  const hasImpoundRecords = impounds.length > 0;
  const hasExportRecords = exports.length > 0;
  const hasRecalls = recalls.length > 0;

  return {
    liens,
    impounds,
    exports,
    stolenRecords,
    ebayAuctions,
    recalls,
    customsInquiries,
    errors,
    hasActiveStolenRecord,
    hasLienRecords,
    hasImpoundRecords,
    hasExportRecords,
    hasRecalls,
    totalRecordsFound: liens.length + impounds.length + exports.length +
      stolenRecords.length + ebayAuctions.length +
      recalls.length + customsInquiries.length,
  };
};

/**
 * Format date for display
 */
export const formatNvsDate = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

/**
 * Get stolen status color based on status value
 */
export const getStolenStatusColor = (status?: string): string => {
  if (!status) return 'text-gray-400';
  switch (status) {
    case 'Active':
      return 'text-red-400';
    case 'Inactive':
      return 'text-yellow-400';
    case 'Purged':
      return 'text-green-400';
    case 'Unknown':
      return 'text-gray-400';
    default:
      return 'text-gray-400';
  }
};

export interface GenerateBulkTheftLienPdfRequest {
  items: NvsBulkSearchItem[];
  requestedBy?: string;
  companyName?: string;
}

/**
 * Generate a combined bulk Theft/Lien PDF from previously searched VINs
 * Downloads the PDF file to the user's browser
 */
export const generateBulkTheftLienPdfReport = async (
  request: GenerateBulkTheftLienPdfRequest
): Promise<void> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NVS.GENERATE_BULK_PDF,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition');
  let fileName = `TheftLien_Bulk_${new Date().toISOString().split('T')[0]}.pdf`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?(.+)"?/);
    if (match?.[1]) fileName = match[1];
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Generate a bulk Theft/Lien PDF and return a blob URL for inline viewing.
 * Caller is responsible for revoking the returned object URL when done.
 */
export const fetchBulkTheftLienPdfForViewing = async (
  request: GenerateBulkTheftLienPdfRequest
): Promise<string> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NVS.GENERATE_BULK_PDF,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const blob = await response.blob();
  return window.URL.createObjectURL(blob);
};

/**
 * Fetch a Theft/Lien PDF as a blob URL for inline viewing (no reference note).
 * Caller is responsible for revoking the returned object URL when done.
 */
export const fetchTheftLienPdfForViewing = async (vin: string): Promise<string> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NVS.GENERATE_PDF,
    {
      method: 'POST',
      body: JSON.stringify({ vin }),
    }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const blob = await response.blob();
  return window.URL.createObjectURL(blob);
};

/**
 * Generate Theft/Lien PDF Request Interface
 */
export interface GenerateTheftLienPdfRequest {
  vin: string;
  requestedBy?: string;
  referenceNote?: string;
  companyName?: string;
}

/**
 * Generate a PDF report for Theft/Lien event data
 * Downloads the PDF file to the user's browser
 */
export const generateTheftLienPdfReport = async (
  request: GenerateTheftLienPdfRequest
): Promise<void> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.NVS.GENERATE_PDF,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    // Get the PDF blob from the response
    const blob = await response.blob();

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = `TheftLien_Report_${request.vin}_${new Date().toISOString().split('T')[0]}.pdf`;

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1];
      }
    }

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw error;
  }
};

export { getErrorMessage };
